<template>
  <section
    class="games-section"
    :class="{ 'games-section--recent': isRecent }"
  >
    <div class="section-header">
      <div class="section-header__title">
        <h2>{{ sectionTitle }}</h2>
        <p v-if="isRecent && sectionSubtitle" class="section-header__subtitle">{{ sectionSubtitle }}</p>
        <div
          v-else-if="showLegend"
          class="section-header__legend"
          :aria-label="sectionTitle"
        >
          <span class="game-badge-legend game-badge-legend--hot">HOT</span>
          <span class="game-badge-legend game-badge-legend--new">NEW</span>
        </div>
      </div>
      <div v-if="carouselItemCount" class="section-header__actions">
        <a
          v-if="carouselShowNav"
          href="#"
          class="section-header__more"
          @click.prevent="toggleCarouselExpanded"
        >{{ carouselExpanded ? $t('games.collapse') : $t('games.expand') }}</a>
        <div v-if="carouselShowNav" class="carousel-nav">
          <button
            type="button"
            class="carousel-nav__btn"
            :disabled="!carouselCanPrev"
            :aria-label="$t('common.prev')"
            @click="carouselPrev"
          >
            ‹
          </button>
          <button
            type="button"
            class="carousel-nav__btn"
            :disabled="!carouselCanNext"
            :aria-label="$t('common.next')"
            @click="carouselNext"
          >
            ›
          </button>
        </div>
      </div>
    </div>

    <p v-if="!isRecent && loading" class="games-section__status">{{ $t('games.loading') }}</p>
    <p v-else-if="!isRecent && loadError" class="games-section__status games-section__status--error">{{ loadError }}</p>
    <p v-else-if="!carouselItems.length" class="games-section__status">{{ emptyMessage }}</p>

    <div
      v-else
      class="games-carousel"
      :class="{
        'games-carousel--step': carouselShowNav && !carouselExpanded,
        'games-carousel--static': carouselExpanded || !carouselShowNav,
        'games-carousel--expanded': carouselExpanded
      }"
    >
      <div
        class="games-carousel__track"
        :style="carouselTrackStyle"
      >
        <article
          v-for="game in carouselItems"
          :key="game.id"
          class="game-card"
          @click="$emit('preview', game.raw)"
        >
          <div class="game-card__thumb">
            <span
              v-if="game.badge"
              class="game-card__badge"
              :class="game.badgeClass"
            >{{ game.badge }}</span>
            <img
              :src="game.img"
              :alt="game.title"
              loading="lazy"
              decoding="async"
              @error="onImgError($event)"
            >
            <div class="game-card__play">
              <i class="fas fa-circle-play" aria-hidden="true" />
            </div>
          </div>
          <div class="game-info">
            <strong>{{ game.title }}</strong>
            <span v-if="game.provider_name">{{ game.provider_name }}</span>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import { getHotGameBadge, normalizeHotGames } from '@/utils/game'
import { normalizeRecentGames } from '@/utils/recentGames'
import gameCarouselStep from '@/mixins/gameCarouselStep'

const FALLBACK_IMG = 'data:image/svg+xml,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="216" height="160" viewBox="0 0 216 160">' +
  '<rect width="216" height="160" fill="#151515"/>' +
  '<text x="108" y="84" fill="#666" font-size="14" text-anchor="middle" font-family="sans-serif">No Image</text>' +
  '</svg>'
)

export default {
  name: 'BlackGoldGames',
  mixins: [gameCarouselStep],
  props: {
    variant: {
      type: String,
      default: 'hot'
    },
    hotGames: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadError: {
      type: String,
      default: ''
    }
  },
  computed: {
    isRecent () {
      return this.variant === 'recent'
    },
    sectionTitle () {
      return this.isRecent ? this.$t('games.recentTitle') : this.$t('games.hotTitle')
    },
    sectionSubtitle () {
      return this.isRecent ? this.$t('games.recentSubtitle') : ''
    },
    showLegend () {
      return !this.isRecent
    },
    emptyMessage () {
      return this.isRecent ? this.$t('games.recentEmpty') : this.$t('games.empty')
    },
    carouselItems () {
      const list = this.isRecent
        ? normalizeRecentGames(this.hotGames)
        : normalizeHotGames(this.hotGames)

      return list.map((game, index) => {
        if (this.isRecent) {
          return {
            id: `recent-${game.provider}-${game.code}-${index}`,
            title: game.name,
            img: game.image || FALLBACK_IMG,
            badge: this.$t('games.recentBadge'),
            badgeClass: 'game-card__badge--recent',
            provider_name: game.provider_name || '',
            raw: game
          }
        }

        const badge = getHotGameBadge(game)
        return {
          id: `hot-${game.provider}-${game.code}-${index}`,
          title: game.name,
          img: game.image || FALLBACK_IMG,
          badge,
          badgeClass: badge === 'NEW' ? 'game-card__badge--new' : 'game-card__badge--hot',
          provider_name: game.provider_name || game.providerLabel || '',
          raw: game
        }
      })
    }
  },
  methods: {
    onImgError (event) {
      if (event && event.target) {
        event.target.src = FALLBACK_IMG
      }
    }
  }
}
</script>
